import {Card, DataStatus} from 'platform/components';
import {Show} from 'platform/foundation';

import type {ReactNode} from 'react';

import {isEmpty} from 'ramda';

import {
  type ConfigurationDetailResponseBody,
  useGetConfigurationByVehicleQuery,
} from '@dms/api/new-vehicle';
import {useGetFieldDefinitionListQuery} from '@dms/api/settings';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {testIds, vehiclesRoutes} from '@dms/routes';
import {EntityCustomFields, Main, usePermissions} from '@dms/shared';

import {composePath} from 'shared/web';

import {ConfigurationTab} from './ConfigurationTab';
import {OrderDeliveryTab} from './OrderDeliveryTab';
import {PriceBasisTab} from './PriceBasisTab';

const t = (key: string, defaultValue: string) => i18n.t(key, {defaultValue});

export function NewVehicle(props: {vehicleId: string}) {
  const [canEditCustomFields] = usePermissions({
    permissionKeys: ['newVehicleConfigurationCustomFields'],
  });
  const detailQuery = useGetConfigurationByVehicleQuery({vehicleId: props.vehicleId});
  // EntityCustomFields renders nothing without tenant field definitions, so the tab says why.
  const definitionsQuery = useGetFieldDefinitionListQuery({
    resourceId: EntityResourceIds.newVehicleConfiguration,
    type: 'tenant',
  });
  const params = {params: {id: props.vehicleId}};
  // Every tab reads the same configuration, so one load state guards them all.
  const withDetail = (render: (detail: ConfigurationDetailResponseBody) => ReactNode) => (
    <DataStatus
      isLoading={detailQuery.isLoading}
      isError={detailQuery.isError}
      errorAction={{title: i18n.t('general.actions.retry'), onClick: detailQuery.refetch}}
    >
      <Show when={detailQuery.currentData}>{render}</Show>
    </DataStatus>
  );

  return (
    <Main
      data-testid={testIds.vehicles.newVehicleDetail('page')}
      tabs={[
        {
          id: 'order',
          title: t('entity.newVehicle.vehicleDetail.orderDeliveryTab', 'Order & delivery'),
          href: composePath(vehiclesRoutes.newVehicleDetail, params),
          content: withDetail((detail) => <OrderDeliveryTab detail={detail} />),
          'data-testid': testIds.vehicles.newVehicleDetail('order'),
        },
        {
          id: 'configuration',
          title: t('entity.newVehicle.labels.configuration', 'Configuration'),
          href: composePath(vehiclesRoutes.newVehicleConfiguration, params),
          content: withDetail((detail) => <ConfigurationTab detail={detail} />),
          'data-testid': testIds.vehicles.newVehicleDetail('configuration'),
        },
        {
          id: 'priceBasis',
          title: t('entity.newVehicle.vehicleDetail.priceBasisTab', 'Price basis'),
          href: composePath(vehiclesRoutes.newVehiclePriceBasis, params),
          content: withDetail((detail) => <PriceBasisTab detail={detail} />),
          'data-testid': testIds.vehicles.newVehicleDetail('priceBasis'),
        },
        {
          id: 'customFields',
          title: t('entity.newVehicle.order.customFields', 'Custom fields'),
          href: composePath(vehiclesRoutes.newVehicleCustomFields, params),
          content: withDetail((detail) => (
            <DataStatus
              isLoading={definitionsQuery.isLoading}
              isError={definitionsQuery.isError}
              errorAction={{
                title: i18n.t('general.actions.retry'),
                onClick: definitionsQuery.refetch,
              }}
            >
              {isEmpty(definitionsQuery.currentData) ? (
                <Card title={t('entity.newVehicle.order.customFields', 'Custom fields')}>
                  <DataStatus
                    isEmpty
                    emptyMessage={i18n.t('page.generalSettings.labels.emptyCustomFieldsMessage')}
                  />
                </Card>
              ) : (
                <EntityCustomFields
                  resourceId={EntityResourceIds.newVehicleConfiguration}
                  recordId={detail.configuration.id}
                  isEditable={Boolean(canEditCustomFields)}
                  data-testid="new-vehicle-configuration-custom-fields"
                />
              )}
            </DataStatus>
          )),
          'data-testid': testIds.vehicles.newVehicleDetail('customFields'),
        },
      ]}
    />
  );
}
